import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb, Card, Form, Row, Col, Button
} from "react-bootstrap";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider,
  ErrorMessages,
  NoData
} from "@whitespace/whitespace-ui";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IRegion from "interfaces/IRegion";
import { makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import "styles/form.scss";
import "styles/form-mixins.scss";
import { normaliseLatLong } from "modules/Normalise";
import IAddDisposalSiteRequestModel from "interfaces/Request/IAddDisposalSiteRequestModel";
import INonWorkingPeriod from "interfaces/INonWorkingPeriod";
import disposalSiteSchema from "modules/schemas/disposalSiteSchema";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const AddDisposalSite = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(true);
  const [regionsDropDownTreeComponent, setRegionsDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [nwpDropDownTreeComponent, setNwpDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [regions, setRegions] = useState<IRegion[] | undefined>();
  const [selectedRegions, setSelectedRegions] = useState<
  string[] | undefined
  >();
  const [nonWorkingPeriods, setNonWorkingPeriods] = useState<INonWorkingPeriod[] | undefined>();
  const [selectedNonWorkingPeriods, setSelectedNonWorkingPeriods] = useState<string[] | undefined>();
  const [regionsDataBoundComplete, setRegionsDataBoundComplete] = useState<boolean>(false);
  const [nonWorkingPeriodDataBoundComplete, setNonWorkingPeriodDataBoundComplete] = useState<boolean>(false);
  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);

  const regionsFields: Object = {
    dataSource: regions,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const nonWorkingPeriodsFields: Object = {
    dataSource: nonWorkingPeriods,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const regionsTreeSettings = { autoCheck: true };

  const nonWorkingPeriodTreeSettings = { autoCheck: false };

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IAddDisposalSiteRequestModel>({
    resolver: yupResolver(disposalSiteSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const watchFields = watch(["active", "name", "regionIds"]);

  useEffect(() => {
    const canFormBeSubmitted = !(
      getValues("name").length > 0
      && selectedRegions
      && selectedRegions.length > 0
    );
    setAddButtonDisabled(canFormBeSubmitted);
  }, [watchFields]);

  const navigate = useNavigate();

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: getNonWorkingPeriodsIsSending,
    errors: getNonWorkingPeriodsErrors,
    data: getNonWorkingPeriodsData,
    sendRequest: getNonWorkingPeriodsSendRequest
  } = useAmplifyREST();

  const {
    isSending: addDisposalSiteIsSending,
    errors: addDisposalSiteErrors,
    statusCode: addDisposalSiteStatusCode,
    sendRequest: addDisposalSiteSendRequest
  } = useAmplifyREST();

  const apiErrors = getRegionsErrors || addDisposalSiteErrors || getNonWorkingPeriodsErrors;

  useEffect(() => {
    setIsSending(
      getRegionsIsSending
        || addDisposalSiteIsSending
        || getNonWorkingPeriodsIsSending
        || !aaFixDropDownTreeCompleted
    );
  }, [
    getRegionsIsSending,
    addDisposalSiteIsSending,
    getNonWorkingPeriodsIsSending,
    aaFixDropDownTreeCompleted
  ]);

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(getRegionsData.regions);
    }
  }, [getRegionsData]);

  useEffect(() => {
    getNonWorkingPeriodsSendRequest("settings/nonworkingperiods", "GET");
  }, [getNonWorkingPeriodsSendRequest]);

  useEffect(() => {
    if (getNonWorkingPeriodsData) {
      setNonWorkingPeriods(getNonWorkingPeriodsData.nonWorkingPeriods);
    }
  }, [getNonWorkingPeriodsData]);

  useEffect(() => {
    if (regionsDataBoundComplete && nonWorkingPeriodDataBoundComplete) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [regionsDataBoundComplete, nonWorkingPeriodDataBoundComplete]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
    }
  }, [apiErrors]);

  const regionsDropDownTreeComponentOnChange = () => {
    setSelectedRegions(regionsDropDownTreeComponent?.value);
  };

  const nonWorkingPeriodsDropDownTreeComponentOnChange = () => {
    setSelectedNonWorkingPeriods(nwpDropDownTreeComponent?.value);
  };

  const handleAddDisposalSiteClick = (
    request: IAddDisposalSiteRequestModel
  ) => {
    request.latitude = request.latitude!.length > 0 ? request.latitude : undefined;
    request.longitude = request.longitude!.length > 0 ? request.longitude : undefined;
    request.regionIds = selectedRegions!;
    request.nonWorkingPeriodIds = selectedNonWorkingPeriods!;
    addDisposalSiteSendRequest("settings/disposalsites", "POST", request);
  };

  useEffect(() => {
    if (addDisposalSiteStatusCode === 200) navigate(RoutePathsEnum.SettingsDisposalSites);
  }, [addDisposalSiteStatusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="disposalsite-title">
              Add Disposal Site
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleAddDisposalSiteClick)}
        >
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb
                  className="left-margin-large no-wrap"
                  test-id="breadcrumb"
                >
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-disposalsites"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsDisposalSites }}
                    onClick={() => navigate(RoutePathsEnum.SettingsDisposalSites)}
                  >
                    Disposal Sites
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-add-disposalsite"
                    active
                  >
                    Add New Disposal Site
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  aria-label="Create Disposal Site"
                  type="submit"
                  spin={addDisposalSiteIsSending}
                  data-testid="create"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsDisposalSites)}
                  data-testid="cancel"
                  aria-label="Cancel creation and back to disposal Sites"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="disposalsites pt-2">
              <Row className="d-flex mt-1 ms-1">
                <Col xs={12}>
                  <div className="control-pane">
                    <div className="control-section" />
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="name">
                        <Form.Label
                          id="siteNameLabel"
                          className="horiz-form-label"
                        >
                          Site Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            aria-labelledby="siteNameLabel"
                            maxLength={50}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="regionIds">
                        <Form.Label
                          id="regionIdsLabel"
                          className="horiz-form-label"
                        >
                          Region(s)
                        </Form.Label>
                        <Col>
                          {regions && (
                            <DropDownTreeComponent
                              {...register("regionIds")}
                              ref={(d: DropDownTreeComponent) => {
                                setRegionsDropDownTreeComponent(d);
                              }}
                              change={regionsDropDownTreeComponentOnChange}
                              dataBound={() => setRegionsDataBoundComplete(true)}
                              treeSettings={regionsTreeSettings}
                              fields={regionsFields}
                              allowMultiSelection
                              showCheckBox
                              changeOnBlur={false}
                              mode="Box"
                              id="regionIdsDropDown"
                              htmlAttributes={{
                                "data-testid": "ddlregionIds",
                                "aria-required": "true"
                              }}
                            />
                          )}
                          <NoData
                            visible={
                              !getRegionsErrors
                              && !getRegionsIsSending
                              && !regions
                            }
                            description="No Regions Found"
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="regions"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="latitude">
                        <Form.Label
                          id="latitudeLabel"
                          className="horiz-form-label"
                        >
                          Latitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("latitude")}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck(
                              "latitude",
                              errors,
                              null
                            )}
                            aria-labelledby="latitudeLabel"
                            maxLength={50}
                            onChange={e => {
                              e.target.value = normaliseLatLong(e.target.value);
                            }}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="latitude"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="longitude">
                        <Form.Label
                          id="longitudeLabel"
                          className="horiz-form-label"
                        >
                          Longitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("longitude")}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck(
                              "longitude",
                              errors,
                              null
                            )}
                            aria-labelledby="longitudeLabel"
                            maxLength={50}
                            onChange={e => {
                              e.target.value = normaliseLatLong(e.target.value);
                            }}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="longitude"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="nonWorkingPeriodIds">
                        <Form.Label
                          id="nonWorkingPeriodIdsLabel"
                          className="horiz-form-label"
                        >
                          Non-Working Periods
                        </Form.Label>
                        <Col>
                          {nonWorkingPeriods && (
                            <DropDownTreeComponent
                              {...register("nonWorkingPeriodIds")}
                              ref={(d: DropDownTreeComponent) => {
                                setNwpDropDownTreeComponent(d);
                              }}
                              change={
                                nonWorkingPeriodsDropDownTreeComponentOnChange
                              }
                              treeSettings={nonWorkingPeriodTreeSettings}
                              fields={nonWorkingPeriodsFields}
                              allowMultiSelection
                              showCheckBox
                              changeOnBlur={false}
                              dataBound={() => setNonWorkingPeriodDataBoundComplete(true)}
                              mode="Box"
                              id="nonWorkingPeriodsDropDown"
                              htmlAttributes={{
                                "aria-required": "true"
                              }}
                            />
                          )}
                          <NoData
                            visible={
                              !getNonWorkingPeriodsErrors
                              && !getNonWorkingPeriodsIsSending
                              && !nonWorkingPeriods
                            }
                            description="No Non-working Periods Found"
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="nonWorkingPeriodIds"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2 mt-3">
                      <Form.Group
                        as={Row}
                        controlId="active"
                        className="d-flex align-items-center"
                      >
                        <Form.Label
                          id="activeLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          Active:
                        </Form.Label>
                        <Form.Check
                          {...register("active")}
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-required="true"
                          aria-labelledby="activeLabel"
                          defaultChecked
                        />
                        <ErrorMessages
                          fieldName="active"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                  </div>
                </Col>
              </Row>
            </section>
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default AddDisposalSite;
